import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { PatientInitialsAvatar } from '@/shared/components/PatientInitialsAvatar';
import { Badge } from '@/components/ui/badge';
import { useMe } from '@/hooks/useMe';

import { useSidebarCollapsed } from './sidebarCollapsed';

/**
 * User identity block for the bottom of the browse sidebar.
 * Hidden until `/me` loads successfully. Collapses to an avatar-only
 * presentation when the sidebar is collapsed.
 */
export function BrowseSidebarUserFooter(): ReactElement | null {
  const { t } = useTranslation();
  const { data, isSuccess } = useMe();
  const collapsed = useSidebarCollapsed();

  if (!isSuccess) {
    return null;
  }

  const subtitle =
    data.title !== null && data.title.length > 0
      ? data.title
      : t(`routes.browse.user.role.${data.role}`);

  if (collapsed) {
    return (
      <div title={data.fullName}>
        <PatientInitialsAvatar
          firstName={data.firstName}
          lastName={data.lastName}
          size="md"
        />
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 min-w-0">
      <PatientInitialsAvatar
        firstName={data.firstName}
        lastName={data.lastName}
        size="md"
      />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-card-foreground truncate">
          {data.fullName}
        </p>
        {subtitle.length > 0 && (
          <p className="text-xs text-muted-foreground truncate">{subtitle}</p>
        )}
        {data.department !== null && data.department.length > 0 && (
          <Badge variant="secondary" className="mt-1 h-5 max-w-full truncate">
            {data.department}
          </Badge>
        )}
      </div>
    </div>
  );
}
